import { cn } from '@invent/wl-ui-kit-next';

import { VeritantWordmark } from '@/components/veritant-wordmark';

/**
 * The wordmark and the name of the site beside it: **Veritant | Advisor Portal**, **Veritant |
 * Team Portal**.
 *
 * Not one lockup. The logo is the client's file and is never combined with another word
 * (`veritant-wordmark.tsx`), so the mark stays whole, in one of its supplied colours and with its
 * own clearspace, and the zone name is set *next to* it, past a hairline rule, in the body face.
 * That is the two-portals design's resolution (§5): the zone is named at a glance without
 * redrawing the mark.
 *
 * `light` is the portal's white bar and the front door (Sapphire mark, Royal Blue name); `dark` is
 * the Team Portal's Midnight rail (white mark, Turquoise name — 7.9:1 on Midnight).
 */
export function Lockup({
  zoneName,
  tone = 'light',
  compact = false,
  className
}: {
  zoneName: string;
  tone?: 'light' | 'dark';
  /** The rail collapsed to its icons: the mark alone, the name kept for a screen reader. */
  compact?: boolean;
  className?: string;
}) {
  return (
    <span className={cn('inline-flex items-center gap-3', className)}>
      <VeritantWordmark
        tone={tone === 'light' ? 'sapphire' : 'white'}
        className={cn(
          '-mx-[0.33em]',
          // The 56 px icon rail holds the mark alone, at the size it fits in with its clearspace:
          // the mark is 4.8 times as wide as tall, so 0.75rem (9 px tall) draws it 42 px across.
          compact ? 'text-[0.75rem]' : tone === 'light' ? 'text-[1.625rem]' : 'text-[1.3rem]'
        )}
      />
      <span
        aria-hidden
        className={cn(
          'h-5 w-px',
          compact && 'hidden',
          tone === 'light'
            ? 'bg-[var(--veritant-royal-15)]'
            : 'bg-[color-mix(in_srgb,var(--color-slight-blue)_35%,transparent)]'
        )}
      />
      <span
        className={cn(
          'text-[0.9375rem] leading-none tracking-[0.01em] whitespace-nowrap',
          compact && 'sr-only',
          tone === 'light'
            ? 'text-[var(--color-royal-blue)]'
            : 'text-[0.8125rem] font-medium text-[var(--color-turquoise)]'
        )}
      >
        {zoneName}
      </span>
    </span>
  );
}
